import Chart, { useChart } from 'src/components/chart';
import { useSelector } from 'react-redux';
import { t } from 'i18next';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { fPercent } from 'src/utils/format-number';
import { generateAnnotations } from 'src/utils/graph-demo-annotations';
import { formatCurrency } from 'src/utils/format-currency';

// ----------------------------------------------------------------------
type Props = {
  series: {
    name: string;
    type: string;
    data: number[];
    color?: string;
  }[];
  labels: string[];
  onLegendClick?: (seriesName: string) => void;
  height?: number;
  showDataLabels?: boolean;
  /** Evolution percentages based on the matching series in the comparison period. */
  comparisonSeries?: { name: string; data: number[] }[];
  showEvolution?: boolean;
  barBorderRadius?: number;
  hideLegend?: boolean;
};

// ----------------------------------------------------------------------
const CHART_COLORS = {
  TWO_SERIES: ['#ff9800', '#5119b7'],
  MULTIPLE_SERIES: ['#5119b7', '#ffb300', '#ffd54f', '#8E33FF'],
};
const PERCENTAGE_SERIES_INDICATORS = ['CTR', 'vulnerability'];
const AD_COST_SERIES_NAMES = [
  'dashboard.brandTraffic.adCost',
  'dashboard.brandTraffic.searchAdCost',
  'dashboard.brandTraffic.shoppingAdCost',
  'dashboard.global.pmax_ad_cost',
];
const DEFAULT_HEIGHT = 450;
const MAX_PERCENTAGE = 100;
const Y_AXIS_MARGIN_MULTIPLIER = 1.3;

// ----------------------------------------------------------------------
const isPercentageData = (name: string): boolean =>
  PERCENTAGE_SERIES_INDICATORS.some((indicator) =>
    name.includes(indicator)
  );
const limitTo100 = (value: number): number =>
  Math.min(value, MAX_PERCENTAGE);

// ----------------------------------------------------------------------
const roundUpToNiceInterval = (value: number): number => {
  if (value <= 0) return 0.5;
  const magnitude = Math.pow(
    10,
    Math.floor(Math.log10(value))
  );
  const normalized = value / magnitude;
  let niceValue;
  if (normalized <= 1.2) niceValue = 1.2;
  else if (normalized <= 1.5) niceValue = 1.5;
  else if (normalized <= 2) niceValue = 2;
  else if (normalized <= 3) niceValue = 3;
  else if (normalized <= 5) niceValue = 5;
  else if (normalized <= 7) niceValue = 7;
  else niceValue = 10;
  return niceValue * magnitude;
};

// ----------------------------------------------------------------------
const getAdCostSeries = (series: any[]) =>
  series.filter((serie) =>
    AD_COST_SERIES_NAMES.some(
      (name) => serie.name === t(name)
    )
  );

// ----------------------------------------------------------------------
const calculateMaxAdCost = (
  adCostSeries: any[]
): number => {
  if (!adCostSeries.length) return 0.5;
  const barSeries = adCostSeries.filter(
    (serie) => serie.type === 'bar'
  );
  const lineSeries = adCostSeries.filter(
    (serie) =>
      serie.type === 'line' ||
      !serie.type
  );
  let maxValue = 0;
  if (barSeries.length > 0) {
    const maxStackedSum = Math.max(
      ...Array.from(
        { length: barSeries[0].data.length },
        (_, i) =>
          barSeries.reduce(
            (sum, serie) =>
              sum + (serie.data[i] || 0),
            0
          )
      )
    );
    maxValue = Math.max(
      maxValue,
      maxStackedSum
    );
  }
  if (lineSeries.length > 0) {
    const maxLineValue = Math.max(
      ...lineSeries.flatMap((serie) =>
        serie.data.filter(
          (value: number) =>
            value !== null &&
            value !== undefined
        )
      )
    );
    maxValue = Math.max(
      maxValue,
      maxLineValue
    );
  }
  if (maxValue === 0) return 0.5;
  return roundUpToNiceInterval(
    maxValue * Y_AXIS_MARGIN_MULTIPLIER
  );
};

// ----------------------------------------------------------------------
const getChartColors = (
  seriesCount: number
): string[] =>
  seriesCount === 2
    ? CHART_COLORS.TWO_SERIES
    : CHART_COLORS.MULTIPLE_SERIES;

// ----------------------------------------------------------------------
const calculateMaxCtr = (
  series: any[]
): number => {
  const ctrSeries = series.find(
    (serie) =>
      serie.name ===
      t('dashboard.global.blended_ctr')
  );
  const values = (
    ctrSeries?.data ?? []
  ).filter(
    (value: number) =>
      value !== null &&
      value !== undefined
  );
  if (!values.length) {
    return MAX_PERCENTAGE;
  }
  const maxValue = Math.max(...values);
  if (
    !Number.isFinite(maxValue) ||
    maxValue <= 0
  ) {
    return MAX_PERCENTAGE;
  }
  return Math.min(
    MAX_PERCENTAGE,
    roundUpToNiceInterval(
      maxValue * Y_AXIS_MARGIN_MULTIPLIER
    )
  );
};

// ----------------------------------------------------------------------
const formatSeriesValue = (
  seriesName: string,
  value: number,
  workspace: any,
  lang: string,
  adCostDecimals: number
): string => {
  if (isPercentageData(seriesName)) {
    return fPercent(
      limitTo100(value)
    );
  }
  if (
    seriesName ===
    t('dashboard.global.blended_cpc')
  ) {
    return formatCurrency(
      value,
      workspace?.currency || 'USD',
      lang,
      false,
      3
    );
  }
  return formatCurrency(
    value,
    workspace?.currency || 'USD',
    lang,
    true,
    adCostDecimals
  );
};

// ----------------------------------------------------------------------
const createTooltipFormatter = (
  workspace: any,
  lang: string
) => {
  return (
    value: number,
    { seriesIndex, w }: any
  ) => {
    if (typeof value === 'undefined') {
      return value;
    }
    // A missing rate (no search volume yet) has no value, not 0%
    if (value === null) {
      return '–';
    }
    const seriesName =
      w.config.series[seriesIndex].name;
    return formatSeriesValue(
      seriesName,
      value,
      workspace,
      lang,
      1
    );
  };
};

// ----------------------------------------------------------------------
const createYAxisConfig = (
  adjustedSeries: any[],
  workspace: any,
  lang: string
) => {
  const hasCtr = adjustedSeries.some(
    (serie) =>
      serie.name ===
      t('dashboard.global.blended_ctr')
  );
  const hasAdCost = adjustedSeries.some(
    (serie) =>
      AD_COST_SERIES_NAMES
        .map((name) => t(name))
        .includes(serie.name)
  );
  const hasCpc = adjustedSeries.some(
    (serie) =>
      serie.name ===
      t('dashboard.global.blended_cpc')
  );
  const yAxis: any[] = [];
  if (hasCtr) {
    yAxis.push({
      title: {
        text: t(
          'dashboard.global.blended_ctr'
        ),
      },
      seriesName: [
        t(
          'dashboard.global.blended_ctr'
        ),
      ],
      opposite: true,
      min: 0,
      max: calculateMaxCtr(
        adjustedSeries
      ),
      labels: {
        formatter(value: number) {
          return `${limitTo100(value).toFixed(0)}%`;
        },
      },
    });
  }
  if (hasAdCost) {
    yAxis.push({
      title: {
        text: t(
          'dashboard.global.ad_cost'
        ),
      },
      seriesName:
        AD_COST_SERIES_NAMES.map(
          (name) => t(name)
        ),
      min: 0,
      max: calculateMaxAdCost(
        getAdCostSeries(
          adjustedSeries
        )
      ),
      labels: {
        formatter(value: number) {
          return formatCurrency(
            value,
            workspace?.currency || 'USD',
            lang,
            true,
            1
          );
        },
      },
    });
  }
  if (hasCpc) {
    yAxis.push({
      title: {
        text: t(
          'dashboard.global.blended_cpc'
        ),
      },
      seriesName: [
        t(
          'dashboard.global.blended_cpc'
        ),
      ],
      min: 0,
      max: (() => {
        const cpcSeries =
          adjustedSeries.find(
            (serie) =>
              serie.name ===
              t(
                'dashboard.global.blended_cpc'
              )
          );
        if (!cpcSeries?.data?.length) {
          return 0.5;
        }
        return roundUpToNiceInterval(
          Math.max(...cpcSeries.data) *
          Y_AXIS_MARGIN_MULTIPLIER
        );
      })(),
      show: false,
      labels: {
        formatter(value: number) {
          return formatCurrency(
            value,
            workspace?.currency || 'USD',
            lang,
            false,
            2
          );
        },
      },
    });
  }
  return yAxis;
};

// ----------------------------------------------------------------------
export default function ChartMixed({
  series,
  labels,
  onLegendClick,
  height = DEFAULT_HEIGHT,
  showDataLabels = false,
  comparisonSeries = [],
  showEvolution = false,
  barBorderRadius = 4,
  hideLegend = false,
}: Props) {
  const filterStore = useSelector(
    (state: any) => state.filters
  );
  const workspace =
    useCurrentWorkspace();
  const chartRef = useRef(null);
  /*
   * Wrapper DOM uniquement pour manipuler
   * les labels SVG de CE graphique.
   *
   * Cela évite de modifier les labels
   * d'un autre ChartMixed présent sur la page.
   */
  const chartContainerRef =
    useRef<HTMLDivElement | null>(null);
  const comparisonContainerRef = useRef<HTMLDivElement | null>(null);
  const comparisonItemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const lang: any =
    localStorage.getItem(
      'i18nextLng'
    );
  const blendedCpcName =
    t('dashboard.global.blended_cpc');
  /*
   * CPC est caché par défaut dans le composant actuel.
   * Il doit donc également être caché côté annotations.
   */
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(
    () => new Set([blendedCpcName])
  );
  const chartDataKey = useMemo(
    () =>
      JSON.stringify({
        labels,
        series: series.map((serie) => ({
          name: serie.name,
          type: serie.type,
          data: serie.data,
        })),
      }),
    [labels, series]
  );
  useLayoutEffect(() => {
    setHiddenSeries((previous) => {
      if (previous.size === 1 && previous.has(blendedCpcName)) {
        return previous;
      }
      return new Set([blendedCpcName]);
    });
  }, [chartDataKey, blendedCpcName]);
  const annotations =
    generateAnnotations(
      workspace,
      filterStore,
      labels
    );

  // --------------------------------------------------------------------
  // Series
  // --------------------------------------------------------------------
  const adjustedSeries =
    series.map((serie) => ({
      ...serie,
      data: serie.data.map(
        (value) =>
          // A missing rate stays a gap (Math.min would turn null into 0%)
          isPercentageData(
            serie.name
          ) && value !== null
            ? limitTo100(value)
            : value
      ),
    }));

  // --------------------------------------------------------------------
  // Colors
  // --------------------------------------------------------------------
  const seriesColors =
    adjustedSeries
      .map(
        (serie: any) =>
          serie.color
      )
      .filter(Boolean);
  const resolvedColors =
    seriesColors.length ===
      adjustedSeries.length
      ? seriesColors
      : getChartColors(
        adjustedSeries.length
      );

  // --------------------------------------------------------------------
  // Evolution (%) - same footer layout as the stacked chart.
  // Match by series name, not by index: some ad-cost series are conditional.
  // --------------------------------------------------------------------
  const visibleSeriesCount = adjustedSeries.filter(
    (serie) => !hiddenSeries.has(serie.name)
  ).length;
  const showComparison =
    showEvolution &&
    Boolean(filterStore?.compare) &&
    comparisonSeries.some((serie) => serie.data.length > 0);
  const comparisonValues = useMemo(
    () =>
      labels.map((_, bucketIndex) =>
        series.map((serie, seriesIndex) => {
          const comparedSeries = comparisonSeries.find(
            (compared) => compared.name === serie.name
          );
          // A missing value has no evolution (Number(null) would read as 0)
          const toNumber = (raw: number | null | undefined) => (raw === null || raw === undefined ? NaN : Number(raw));
          const current = toNumber(serie.data[bucketIndex]);
          const previous = toNumber(comparedSeries?.data[bucketIndex]);
          let value: number | null = null;
          if (
            comparedSeries &&
            Number.isFinite(current) &&
            Number.isFinite(previous)
          ) {
            if (previous !== 0) {
              value = ((current - previous) / Math.abs(previous)) * 100;
            } else if (current === 0) {
              value = 0;
            }
          }
          return {
            name: serie.name,
            color: serie.color || resolvedColors[seriesIndex] || '#637381',
            value,
          };
        })
      ),
    [labels, series, comparisonSeries, resolvedColors]
  );
  const formatComparisonValue = (value: number | null): string => {
    if (value === null) return '—';
    if (value > 999) return '>999%';
    if (value < -999) return '<-999%';
    return `${value > 0 ? '+' : ''}${Math.round(value)}%`;
  };
  const comparisonPositionUpdateRef = useRef<() => void>(() => { });
  useLayoutEffect(() => {
    if (!showComparison) return undefined;
    const container = chartContainerRef.current;
    const footer = comparisonContainerRef.current;
    if (!container || !footer) return undefined;
    let animationFrameId: number | null = null;
    let deferredUpdateId: number | null = null;
    const updatePositions = () => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        const xAxisLabels = container.querySelectorAll<SVGTextElement>(
          '.apexcharts-xaxis-texts-g .apexcharts-xaxis-label'
        );
        const footerRect = footer.getBoundingClientRect();
        comparisonItemRefs.current.forEach((element, index) => {
          const label = xAxisLabels[index];
          if (!element || !label) return;
          const rect = label.getBoundingClientRect();
          element.style.left = `${rect.left + rect.width / 2 - footerRect.left}px`;
        });
      });
    };
    comparisonPositionUpdateRef.current = updatePositions;
    updatePositions();
    // Apex can update the SVG after React's initial layout pass.
    deferredUpdateId = window.setTimeout(updatePositions, 200);
    const resizeObserver = new ResizeObserver(updatePositions);
    resizeObserver.observe(container);
    resizeObserver.observe(footer);
    window.addEventListener('resize', updatePositions);
    return () => {
      comparisonPositionUpdateRef.current = () => { };
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      if (deferredUpdateId !== null) window.clearTimeout(deferredUpdateId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePositions);
    };
  }, [showComparison, labels, comparisonValues, hiddenSeries]);

  // --------------------------------------------------------------------
  // Y Axis indexes
  // --------------------------------------------------------------------
  const hasCtr = adjustedSeries.some(
    (serie) =>
      serie.name ===
      t('dashboard.global.blended_ctr')
  );
  const hasAdCost =
    adjustedSeries.some((serie) =>
      AD_COST_SERIES_NAMES
        .map((name) => t(name))
        .includes(serie.name)
    );
  const hasCpc = adjustedSeries.some(
    (serie) =>
      serie.name ===
      blendedCpcName
  );
  let nextAxisIndex = 0;
  const ctrAxisIndex = hasCtr
    ? nextAxisIndex++
    : 0;
  const adCostAxisIndex = hasAdCost
    ? nextAxisIndex++
    : 0;
  const cpcAxisIndex = hasCpc
    ? nextAxisIndex++
    : 0;
  const MIN_LABEL_SEGMENT_HEIGHT = 10;

  // --------------------------------------------------------------------
  // Annotation labels
  // --------------------------------------------------------------------
  const allLabelAnnotations =
    showDataLabels
      ? adjustedSeries.flatMap(
        (
          serie: any,
          seriesIndex: number
        ) => {
          /*
           * Série cachée = aucun label.
           */
          if (
            hiddenSeries.has(
              serie.name
            )
          ) {
            return [];
          }
          return serie.data
            .map(
              (
                value: number,
                dataPointIndex: number
              ) => {
                if (
                  value === null ||
                  value === undefined ||
                  !Number.isFinite(
                    Number(value)
                  ) ||
                  Number(value) <= 0
                ) {
                  return null;
                }
                const isBar =
                  serie.type === 'bar';
                const isCtr =
                  serie.name ===
                  t(
                    'dashboard.global.blended_ctr'
                  );
                const isCpc =
                  serie.name ===
                  blendedCpcName;

                // ----------------------------------------------------
                // OFFSET Y
                //
                // Tous les labels restent attachés à leur data point.
                //
                // Bar  -> juste au-dessus du sommet
                // Line -> juste au-dessus de la ligne
                // ----------------------------------------------------
                let offsetY = -10;
                if (isBar) {
                  // Top de la barre
                  offsetY = 7;
                } else {
                  // Au-dessus du point de la line
                  offsetY = -3;
                }
                /*
                 * Autres éventuelles lines.
                 */
                if (
                  !isBar &&
                  !isCtr &&
                  !isCpc
                ) {
                  offsetY =
                    seriesIndex % 2 === 0
                      ? -24
                      : 24;
                }

                // ----------------------------------------------------
                // Axis
                // ----------------------------------------------------
                let yAxisIndex =
                  adCostAxisIndex;
                if (isCtr) {
                  yAxisIndex =
                    ctrAxisIndex;
                }
                if (isCpc) {
                  yAxisIndex =
                    cpcAxisIndex;
                }

                // ----------------------------------------------------
                return {
                  x: labels[
                    dataPointIndex
                  ],
                  y: Number(value),
                  yAxisIndex,
                  /*
                   * IMPORTANT :
                   *
                   * On NE SE SERT PAS du marker Apex.
                   * Sinon le point est attaché au point du graphe
                   * et non à l'intérieur du label.
                   */
                  marker: {
                    size: 0,
                    strokeWidth: 0,
                    strokeColor: 'transparent',
                    fillColor: 'transparent',
                  },
                  label: {
                    text:
                      formatSeriesValue(
                        serie.name,
                        Number(value),
                        workspace,
                        lang,
                        0
                      ),
                    offsetY,
                    borderColor:
                      'rgba(145, 158, 171, 0.20)',
                    borderWidth: 1,
                    borderRadius: 4,
                    style: {
                      background:
                        '#FFFFFF',
                      color:
                        '#1C252E',
                      fontSize:
                        '11px',
                      fontWeight:
                        500,
                      fontFamily:
                        'Public Sans, sans-serif',
                      /*
                       * Cette classe permet ensuite
                       * d'identifier la série et d'ajouter
                       * le point à l'intérieur du label.
                       */
                      cssClass:
                        `chart-data-label chart-data-label-series-${seriesIndex} chart-data-label-point-${dataPointIndex}`,
                    },
                  },
                };
              }
            )
            .filter(Boolean);
        }
      )
      : [];

  // --------------------------------------------------------------------
  // Inject colored dot INSIDE annotation labels
  // --------------------------------------------------------------------
  const injectSeriesDotsIntoLabels = () => {
    requestAnimationFrame(() => {
      const container = chartContainerRef.current;
      if (!container) return;
      // Nettoie les points éventuellement ajoutés lors d'un précédent rendu
      container
        .querySelectorAll('.chart-label-dot')
        .forEach((dot) => dot.remove());
      adjustedSeries.forEach(
        (serie: any, seriesIndex: number) => {
          // Série cachée => aucun point
          if (hiddenSeries.has(serie.name)) {
            return;
          }
          const seriesColor =
            serie.color ||
            resolvedColors[seriesIndex] ||
            '#637381';
          const textElements =
            container.querySelectorAll(
              `text.chart-data-label-series-${seriesIndex}`
            );
          textElements.forEach((node) => {
            const text = node as SVGTextElement;

            // ----------------------------------------------------------
            // Trouver le rect correspondant exactement à CE texte
            // ----------------------------------------------------------
            let rect =
              text.previousElementSibling as SVGRectElement | null;
            if (
              !rect ||
              rect.tagName.toLowerCase() !== 'rect'
            ) {
              const parent = text.parentElement;
              if (!parent) return;
              const siblings =
                Array.from(parent.children);
              const textIndex =
                siblings.indexOf(text);
              for (
                let i = textIndex - 1;
                i >= 0;
                i -= 1
              ) {
                if (
                  siblings[i].tagName.toLowerCase() ===
                  'rect'
                ) {
                  rect =
                    siblings[i] as SVGRectElement;
                  break;
                }
              }
            }
            if (!rect) return;

            // ----------------------------------------------------------
            // Hide the WHOLE annotation when a rendered BAR segment
            // is less than 10px high.
            //
            // Line-series annotations are not affected by this rule.
            // ----------------------------------------------------------
            const pointClass =
              Array.from(text.classList).find(
                (className) =>
                  className.startsWith(
                    'chart-data-label-point-'
                  )
              );
            const dataPointIndex =
              pointClass
                ? Number(
                  pointClass.replace(
                    'chart-data-label-point-',
                    ''
                  )
                )
                : null;
            if (
              serie.type === 'bar' &&
              dataPointIndex !== null &&
              Number.isFinite(dataPointIndex)
            ) {
              const barSeriesIndex =
                adjustedSeries
                  .slice(0, seriesIndex + 1)
                  .filter(
                    (currentSerie: any) =>
                      currentSerie.type === 'bar'
                  ).length - 1;
              const seriesGroup =
                container.querySelectorAll<SVGGElement>(
                  '.apexcharts-bar-series .apexcharts-series'
                )[barSeriesIndex];
              const bars =
                seriesGroup?.querySelectorAll<SVGPathElement>(
                  '.apexcharts-bar-area'
                );
              const bar =
                seriesGroup?.querySelector<SVGPathElement>(
                  `.apexcharts-bar-area[j="${dataPointIndex}"]`
                ) ??
                bars?.[dataPointIndex] ??
                null;
              if (bar) {
                const barHeightAttribute =
                  bar.getAttribute('barHeight');
                let segmentHeight =
                  barHeightAttribute !== null
                    ? Number(barHeightAttribute)
                    : Number.NaN;
                if (!Number.isFinite(segmentHeight)) {
                  try {
                    segmentHeight =
                      bar.getBBox().height;
                  } catch {
                    segmentHeight =
                      Number.POSITIVE_INFINITY;
                  }
                }
                const shouldHide =
                  Number.isFinite(segmentHeight) &&
                  Math.abs(segmentHeight) <
                  MIN_LABEL_SEGMENT_HEIGHT;
                if (shouldHide) {
                  text.setAttribute(
                    'display',
                    'none'
                  );
                  rect.setAttribute(
                    'display',
                    'none'
                  );
                  return;
                }
              }
            }
            // Restore annotations that may have been hidden before
            // a resize, redraw or legend update.
            text.removeAttribute('display');
            rect.removeAttribute('display');

            // ----------------------------------------------------------
            // Design
            //
            // padding: 4px 8px
            // gap: 4px
            // point: 6px
            // ----------------------------------------------------------
            const PADDING_X = 8;
            const PADDING_Y = 4;
            const GAP = 4;
            const DOT_RADIUS = 3;
            const DOT_SIZE =
              DOT_RADIUS * 2;

            // ----------------------------------------------------------
            // Dimensions originales Apex
            //
            // Important : on les stocke pour ne pas recalculer à partir
            // du chip déjà modifié lors d'un "updated".
            // ----------------------------------------------------------
            const originalXAttribute =
              rect.getAttribute(
                'data-original-x'
              );
            const originalYAttribute =
              rect.getAttribute(
                'data-original-y'
              );
            const originalWidthAttribute =
              rect.getAttribute(
                'data-original-width'
              );
            const originalHeightAttribute =
              rect.getAttribute(
                'data-original-height'
              );
            const originalX =
              originalXAttribute !== null
                ? Number(originalXAttribute)
                : Number(
                  rect.getAttribute('x') || 0
                );
            const originalY =
              originalYAttribute !== null
                ? Number(originalYAttribute)
                : Number(
                  rect.getAttribute('y') || 0
                );
            const originalWidth =
              originalWidthAttribute !== null
                ? Number(originalWidthAttribute)
                : Number(
                  rect.getAttribute('width') || 0
                );
            const originalHeight =
              originalHeightAttribute !== null
                ? Number(originalHeightAttribute)
                : Number(
                  rect.getAttribute('height') || 0
                );
            if (originalXAttribute === null) {
              rect.setAttribute(
                'data-original-x',
                String(originalX)
              );
            }
            if (originalYAttribute === null) {
              rect.setAttribute(
                'data-original-y',
                String(originalY)
              );
            }
            if (originalWidthAttribute === null) {
              rect.setAttribute(
                'data-original-width',
                String(originalWidth)
              );
            }
            if (originalHeightAttribute === null) {
              rect.setAttribute(
                'data-original-height',
                String(originalHeight)
              );
            }

            // ----------------------------------------------------------
            // Centre original du label
            //
            // On conserve ce centre pour que le chip complet reste
            // exactement centré sur son point/barre.
            // ----------------------------------------------------------
            const centerX =
              originalX +
              originalWidth / 2;
            const centerY =
              originalY +
              originalHeight / 2;

            // ----------------------------------------------------------
            // Taille réelle du texte
            // ----------------------------------------------------------
            let textBBox;
            try {
              textBBox =
                text.getBBox();
            } catch {
              return;
            }
            const textWidth =
              textBBox.width;
            const textHeight =
              textBBox.height;

            // ----------------------------------------------------------
            // Taille du chip final
            //
            // 8px padding
            // + 6px point
            // + 4px gap
            // + texte
            // + 8px padding
            // ----------------------------------------------------------
            const chipWidth =
              PADDING_X +
              DOT_SIZE +
              GAP +
              textWidth +
              PADDING_X;
            const chipHeight =
              PADDING_Y +
              textHeight +
              PADDING_Y;

            // ----------------------------------------------------------
            // Position du chip
            //
            // Le chip ENTIER est centré sur la position Apex originale.
            // ----------------------------------------------------------
            const chipX =
              centerX -
              chipWidth / 2;
            const chipY =
              centerY -
              chipHeight / 2;
            rect.setAttribute(
              'x',
              String(chipX)
            );
            rect.setAttribute(
              'y',
              String(chipY)
            );
            rect.setAttribute(
              'width',
              String(chipWidth)
            );
            rect.setAttribute(
              'height',
              String(chipHeight)
            );
            rect.setAttribute(
              'rx',
              '4'
            );
            rect.setAttribute(
              'ry',
              '4'
            );

            // ----------------------------------------------------------
            // Point
            //
            // 8px padding gauche
            // puis point de 6px
            // ----------------------------------------------------------
            const dotCenterX =
              chipX +
              PADDING_X +
              DOT_RADIUS;
            const dotCenterY =
              centerY;

            // ----------------------------------------------------------
            // Texte
            //
            // point + 4px gap
            // ----------------------------------------------------------
            const textStartX =
              chipX +
              PADDING_X +
              DOT_SIZE +
              GAP;
            text.setAttribute(
              'text-anchor',
              'start'
            );
            text.setAttribute(
              'x',
              String(textStartX)
            );
            text.setAttribute(
              'y',
              String(centerY)
            );
            text.setAttribute(
              'dominant-baseline',
              'middle'
            );

            // ----------------------------------------------------------
            // Création du point
            // ----------------------------------------------------------
            const circle =
              document.createElementNS(
                'http://www.w3.org/2000/svg',
                'circle'
              );
            circle.setAttribute(
              'class',
              `chart-label-dot chart-label-dot-series-${seriesIndex}`
            );
            circle.setAttribute(
              'cx',
              String(dotCenterX)
            );
            circle.setAttribute(
              'cy',
              String(dotCenterY)
            );
            circle.setAttribute(
              'r',
              String(DOT_RADIUS)
            );
            circle.setAttribute(
              'fill',
              seriesColor
            );
            circle.setAttribute(
              'stroke',
              'none'
            );
            // Point devant le background, juste avant le texte
            text.parentNode?.insertBefore(
              circle,
              text
            );
          });
        }
      );
    });
  };

  // --------------------------------------------------------------------
  // Chart options
  // --------------------------------------------------------------------
  const chartOptions =
    useChart({
      plotOptions: {
        bar: {
          columnWidth: '65%',
          borderRadius:
            barBorderRadius,
          borderRadiusApplication:
            'end',
          borderRadiusWhenStacked:
            'last',
        },
      },
      /*
       * Aucun dataLabel Apex natif.
       * Tout est géré par annotations.points.
       */
      dataLabels: {
        enabled: false,
      },
      chart: {
        stacked: true,
        height: showComparison ? Math.max(height - Math.max(visibleSeriesCount, 1) * 16, 160) : height,
        events: {

          // ------------------------------------------------------------
          // First render
          // ------------------------------------------------------------
          mounted: (
            chartContext: any
          ) => {
            try {
              /*
               * CPC caché par défaut.
               */
              if (
                chartContext?.hideSeries
              ) {
                chartContext.hideSeries(
                  blendedCpcName
                );
              }
            } catch (e) {
              console.warn(
                'Impossible de cacher la série blended_cpc :',
                e
              );
            }
            /*
             * Le hideSeries peut déclencher son propre update,
             * donc on laisse Apex finir son cycle.
             */
            requestAnimationFrame(
              () => {
                injectSeriesDotsIntoLabels();
                comparisonPositionUpdateRef.current();
              }
            );
          },
          animationEnd: () => {
            injectSeriesDotsIntoLabels();
            comparisonPositionUpdateRef.current();
          },

          // ------------------------------------------------------------
          // Every Apex rerender
          // ------------------------------------------------------------
          updated: () => {
            injectSeriesDotsIntoLabels();
            comparisonPositionUpdateRef.current();
          },

          // ------------------------------------------------------------
          // Legend hide / show
          // ------------------------------------------------------------
          legendClick: (
            chartContext: any,
            seriesIndex: number,
            config: any
          ) => {
            const seriesName =
              config?.config
                ?.series?.[
                seriesIndex
              ]?.name;
            if (!seriesName) {
              return;
            }
            /*
             * Apex gère lui-même le hide/show
             * de la série graphique.
             *
             * Ici on synchronise juste nos annotations.
             */
            setHiddenSeries(
              (previous) => {
                const next =
                  new Set(
                    previous
                  );
                if (
                  next.has(
                    seriesName
                  )
                ) {
                  /*
                   * Série actuellement cachée :
                   * elle va être affichée.
                   */
                  next.delete(
                    seriesName
                  );
                } else {
                  /*
                   * Série actuellement visible :
                   * elle va être cachée.
                   */
                  next.add(
                    seriesName
                  );
                }
                return next;
              }
            );
            onLegendClick?.(
              seriesName
            );
          },
        },
      },

      // ----------------------------------------------------------------
      // Series appearance
      // ----------------------------------------------------------------
      fill: {
        colors:
          resolvedColors,
      },
      stroke: {
        width:
          adjustedSeries.map(
            (serie: any) =>
              serie.type ===
                'bar'
                ? 0
                : 2
          ),
      },
      colors:
        resolvedColors,
      labels:
        labels || [],
      markers: {
        size: 0,
      },

      // ----------------------------------------------------------------
      // Legend
      // ----------------------------------------------------------------
      legend: {
        show: !hideLegend,
        position: 'top',
        horizontalAlign:
          'right',
        fontSize: '13px',
        itemMargin: {
          horizontal: 6,
        },
        markers: {
          customHTML() {
            return `
              <div
                style="
                  width: 8px;
                  height: 8px;
                  border-radius: 50%;
                  background-color: currentcolor;
                "
              ></div>
            `;
          },
        },
        labels: {
          colors:
            '#637381',
        },
      },

      // ----------------------------------------------------------------
      // X Axis
      // ----------------------------------------------------------------
      xaxis: {
        type: 'category',
        tickAmount: labels.length,
        labels: {
          offsetY: 4,
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: false,
        },
      },

      // ----------------------------------------------------------------
      // Y Axis
      // ----------------------------------------------------------------
      yaxis:
        createYAxisConfig(
          adjustedSeries,
          workspace,
          lang
        ),

      // ----------------------------------------------------------------
      // Grid
      // ----------------------------------------------------------------
      grid: {
        padding: {
          top: 28,
          bottom: 8,
        },
      },

      // ----------------------------------------------------------------
      // Tooltip
      // ----------------------------------------------------------------
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({
          series,
          dataPointIndex,
          w,
        }) {
          if (
            !series ||
            !w.globals.seriesNames ||
            !w.globals.colors
          ) {
            return '';
          }
          const date =
            labels[dataPointIndex] || '';
          const formatter =
            createTooltipFormatter(
              workspace,
              lang
            );
          const collapsed = [
            ...(w.globals.collapsedSeriesIndices || []),
            ...(w.globals.ancillaryCollapsedSeriesIndices || []),
          ];
          const rows = series
            .map(
              (
                s: any,
                i: number
              ) => {
                if (collapsed.includes(i)) {
                  return '';
                }
                const value =
                  s[dataPointIndex];
                const formattedValue =
                  formatter(
                    value,
                    {
                      seriesIndex: i,
                      w,
                    }
                  );
                return `
            <div
              style="
                display:flex;
                align-items:center;
                width:100%;
                box-sizing:border-box;
                padding:4px;
                gap:8px;
              "
            >
              <span
                style="
                  display:inline-block;
                  width:4px;
                  height:20px;
                  border-radius:12px;
                  background-color:${w.globals.colors[i]};
                  flex-shrink:0;
                "
              ></span>
              <div
                style="
                  flex:1;
                  min-width:0;
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  font-family:Public Sans,sans-serif;
                  font-size:13px;
                  font-weight:500;
                  color:#333C47;
                  white-space:nowrap;
                  gap:16px;
                "
              >
                <span>
                  ${w.globals.seriesNames[i]}
                </span>
                <span
                  style="
                    flex-shrink:0;
                    text-align:right;
                  "
                >
                  ${formattedValue ?? ''}
                </span>
              </div>
            </div>
          `;
              }
            )
            .join('');
          return `
      <div
        style="
          width:320px;
          min-width:320px;
          max-width:320px;
          box-sizing:border-box;
          background:#FCFCFD;
          border:none;
          border-radius:12px;
          box-shadow:0px 4px 8px rgba(145,158,171,0.16);
          overflow:hidden;
        "
      >
        <div
          style="
            padding:12px 12px 4px 12px;
            box-sizing:border-box;
          "
        >
          <p
            style="
              font-family:Public Sans,sans-serif;
              color:#333C47;
              font-size:14px;
              font-weight:500;
              line-height:14px;
              white-space:nowrap;
              margin:0;
              margin-bottom:12px;
            "
          >
            ${date}
          </p>
        </div>
        <div
          style="
            padding:0 12px 12px 12px;
            display:flex;
            flex-direction:column;
            box-sizing:border-box;
          "
        >
          ${rows}
        </div>
      </div>
    `;
        },
        y: {
          formatter:
            createTooltipFormatter(
              workspace,
              lang
            ),
        },
      },

      // ----------------------------------------------------------------
      // Annotations
      // ----------------------------------------------------------------
      annotations: {
        xaxis: annotations,
        points:
          allLabelAnnotations,
      },
    });

  // --------------------------------------------------------------------
  const comparisonFooterHeight = Math.max(visibleSeriesCount, 1) * 16;
  const renderedChartHeight = showComparison
    ? Math.max(height - comparisonFooterHeight, 160)
    : height;
  return (
    <Box ref={chartContainerRef} sx={{ width: '100%' }}>
      <Chart
        key={chartDataKey}
        ref={chartRef}
        dir="ltr"
        type="line"
        series={adjustedSeries}
        options={chartOptions}
        sx={{
          width: '100%',
          mt: 0.5,
          '& .apexcharts-legend-group-vertical': {
            flexDirection: 'row',
          },
          '& .chart-data-label': {
            filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.04))',
          },
        }}
        height={renderedChartHeight}
      />
      {showComparison && (
        <Box
          ref={comparisonContainerRef}
          sx={{ position: 'relative', width: '100%', height: `${comparisonFooterHeight}px`, mt: '-2px' }}
        >
          {comparisonValues.map((bucketValues, bucketIndex) => (
            <Box
              key={`comparison-bucket-${bucketIndex}`}
              ref={(element: HTMLDivElement | null) => {
                comparisonItemRefs.current[bucketIndex] = element;
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                transform: 'translateX(-50%)',
                width: '76px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '2px',
              }}
            >
              {bucketValues
                .filter((metric) => !hiddenSeries.has(metric.name))
                .map((metric) => {
                  const value = metric.value;
                  const textColor =
                    value === null || value === 0
                      ? '#919EAB'
                      : value > 0
                        ? '#22A06B'
                        : '#FF5630';
                  return (
                    <Box
                      key={`${bucketIndex}-${metric.name}`}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        minHeight: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {bucketIndex === 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            right: '46px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: metric.color,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Box
                        component="span"
                        sx={{
                          width: '42px',
                          textAlign: 'right',
                          fontVariantNumeric: 'lining-nums tabular-nums ordinal',
                          fontFamily: 'Public Sans',
                          fontSize: '12px',
                          color: textColor,
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '120%',
                        }}
                      >
                        {formatComparisonValue(value)}
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}